import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Edit2, Check, X } from 'lucide-react';
import { isProjectOwner } from '../../lib/permissions';

interface ProjectHeaderProps {
  project: {
    id: string;
    title: string;
    description: string;
    status: {
      id: string;
      name: string;
    };
  };
  onProjectUpdate: (project: any) => void;
}

export function ProjectHeader({ project, onProjectUpdate }: ProjectHeaderProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(project.title);
  const [description, setDescription] = useState(project.description);
  const [status, setStatus] = useState(project.status);
  const [statuses, setStatuses] = useState<Array<{ id: string; name: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [canEdit, setCanEdit] = useState(false);

  useEffect(() => {
    fetchStatuses();
    checkPermissions();
  }, [project.id]);

  async function checkPermissions() {
    const isOwner = await isProjectOwner(project.id);
    setCanEdit(isOwner);
  }

  async function fetchStatuses() {
    const { data } = await supabase
      .from('project_statuses')
      .select('id, name')
      .order('name');
    if (data) {
      setStatuses(data);
    }
  }

  async function handleSave() {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('interview_projects')
        .update({
          title,
          description,
          status_id: status.id
        })
        .eq('id', project.id)
        .select(`
          id,
          title,
          description,
          status:status_id (
            id,
            name
          )
        `)
        .single();

      if (error) throw error;
      if (data) {
        onProjectUpdate(data);
      }
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating project:', error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="border-b border-gray-200 pb-5">
      <div className="sm:flex sm:items-baseline sm:justify-between">
        <div className="sm:w-0 sm:flex-1">
          {isEditing ? (
            <div className="space-y-4">
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-colors sm:text-3xl"
              />
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-colors sm:text-sm"
              />
              <select
                value={status.id}
                onChange={(e) => {
                  const selectedStatus = statuses.find(s => s.id === e.target.value);
                  if (selectedStatus) {
                    setStatus(selectedStatus);
                  }
                }}
                className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-colors sm:text-sm"
              >
                {statuses.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold leading-7 text-brand-teal sm:truncate sm:text-3xl">
                {project.title}
              </h1>
              <p className="mt-2 text-sm text-gray-500">{project.description}</p>
              <div className="mt-1 flex flex-col sm:mt-0 sm:flex-row sm:flex-wrap sm:space-x-6">
                <div className="mt-2 flex items-center text-sm text-gray-500">
                  Status: {project.status.name}
                </div>
              </div>
            </>
          )}
        </div>
        <div className="mt-4 flex sm:ml-4 sm:mt-0">
          {canEdit && (
            isEditing ? (
              <>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={loading}
                  className="ml-3 inline-flex items-center rounded-lg border border-transparent bg-brand-orange px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-orange-light focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-offset-2 transition-colors disabled:opacity-50"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setTitle(project.title);
                    setDescription(project.description);
                    setStatus(project.status);
                    setIsEditing(false);
                  }}
                  className="ml-3 inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-offset-2 transition-colors"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                className="ml-3 inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-offset-2 transition-colors"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                Edit
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
}